import { moveSlot, setSlotRoundActive, type Draft } from "@heraion/shared";

import type { TFn } from "../i18nTypes";
import type { SheetMenuItem } from "../primitives/SheetMenu";
import type { V3ExerciseActions } from "./useV3ExerciseActions";

// The exercise menu of the phone editor (#336): move, replace, superset
// (pair, or rename and dissolve), out of the open round, note, copy to other
// days, remove.
// A move that would do nothing, and a round removal that would be refused,
// are disabled in advance; the refusal says why under the label.
export function exerciseMenuItems({
  t,
  draft,
  sIdx,
  slotIdx,
  openRound,
  actions,
  onReplace,
  onPair,
  onRename,
  onNote,
  onCopyToDays,
}: {
  t: TFn;
  draft: Draft;
  sIdx: number;
  slotIdx: number;
  /** The round expanded in the ladder, if any. */
  openRound: number | null;
  actions: V3ExerciseActions;
  onReplace: () => void;
  onPair: () => void;
  onRename: () => void;
  onNote: () => void;
  /** Opens the multi-day tray (CopyOntoTray `multiple`). */
  onCopyToDays: () => void;
}): SheetMenuItem[] {
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const groupId = slots[slotIdx]?.group?.id;
  // Superset-aware: a no-op move (an edge, or a group at the edge) is disabled.
  const canMove = (delta: -1 | 1) => moveSlot(draft, sIdx, slotIdx, delta) !== draft;

  const superset: SheetMenuItem[] = groupId
    ? [
        {
          key: "rename",
          icon: "edit",
          label: t("editorV3.renameSuperset", { defaultValue: "Rename superset" }),
          onPress: onRename,
        },
        {
          key: "dissolve",
          icon: "scissors",
          label: t("editorV3.dissolveSuperset", { defaultValue: "Dissolve superset" }),
          onPress: () => actions.dissolve(groupId),
        },
      ]
    : [
        {
          key: "pair",
          icon: "link",
          label: t("editorV3.supersetWithNext", { defaultValue: "Superset with the next exercise" }),
          onPress: onPair,
          disabled: slotIdx >= slots.length - 1,
        },
      ];

  const offRound: SheetMenuItem[] = [];
  if (openRound !== null) {
    const n = openRound + 1;
    const res = setSlotRoundActive(draft, sIdx, slotIdx, openRound, false);
    const hint = res.ok
      ? undefined
      : res.reason === "lastInDay"
        ? t("editorV3.offRoundOnlyInDay", { n, defaultValue: "The only exercise of this day in W{{n}}" })
        : t("editorV3.offRoundOnlyRound", { defaultValue: "It runs in no other round" });
    offRound.push({
      key: "offRound",
      icon: "minus",
      label: t("editorV3.removeFromRound", { n, defaultValue: "Remove from W{{n}}" }),
      onPress: () => actions.setRoundActive(slotIdx, openRound, false),
      disabled: !res.ok,
      hint,
    });
  }

  return [
    {
      key: "up",
      icon: "chevron-up",
      label: t("program.moveUp", { defaultValue: "Move up" }),
      onPress: () => actions.move(slotIdx, -1),
      disabled: !canMove(-1),
    },
    {
      key: "down",
      icon: "chevron-down",
      label: t("program.moveDown", { defaultValue: "Move down" }),
      onPress: () => actions.move(slotIdx, 1),
      disabled: !canMove(1),
    },
    {
      key: "replace",
      icon: "arrow-left-right",
      label: t("editorV3.replaceExercise", { defaultValue: "Replace exercise" }),
      onPress: onReplace,
    },
    ...superset,
    ...offRound,
    {
      key: "note",
      icon: "note",
      label: t("composer.exerciseNote", { defaultValue: "Exercise note" }),
      onPress: onNote,
    },
    {
      key: "copyDays",
      icon: "copy",
      label: t("editorV3.copyToOtherDays", { defaultValue: "Copy to other days" }),
      onPress: onCopyToDays,
      disabled: draft.sessions.length < 2,
    },
    {
      key: "remove",
      icon: "trash",
      label: t("plans.removeExercise", { defaultValue: "Remove exercise" }),
      onPress: () => actions.remove(slotIdx),
      destructive: true,
    },
  ];
}
